import { fetchConfig, onDocumentReady } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

const CART_UUID_ATTRIBUTE = 'cart_uuid';
const CHECKOUT_SUBMITTER_NAME = 'checkout';
const LOG_PREFIX = '[custom-cart-metadata]';
/* Im Livebetrieb aus. Fuer die Fehlersuche am Mahlgrad-Pfad hier auf true
   setzen, dann protokolliert logDebug jeden Warenkorbvorgang mitsamt der
   berechneten Note. Fehler gehen ueber logError unabhaengig davon raus. */
const DEBUG = false;

const MAHLGRAD_PROPERTY = 'Mahlgrad';
const NOTE_PREFIX = `${MAHLGRAD_PROPERTY}:`;
const NOTE_SEPARATOR = ' | ';
/* Muss zu NOTE_SEPARATOR passen. Als Literal, damit das Pipe-Zeichen nicht
   versehentlich als Regex-Alternative endet. */
const NOTE_SPLITTER = /\s*\|\s*/;

const TRIGGER = {
  pageLoad: 'page-load',
  cartLinesUpdate: 'cart-lines-update',
  checkout: 'checkout',
};

/** @type {Promise<CartMetadata> | null} */
let pendingSync = null;

/** @type {WeakSet<HTMLFormElement>} */
const formsAwaitingResubmit = new WeakSet();

/**
 * @typedef {object} CartMetadata
 * @property {string | null} uuid
 * @property {string | null} note
 */

/**
 * @param {string} message
 * @param {Record<string, unknown>} [data]
 */
function logDebug(message, data) {
  if (!DEBUG) return;

  if (data) console.log(`${LOG_PREFIX} ${message}`, data);
  else console.log(`${LOG_PREFIX} ${message}`);
}

/**
 * @param {string} context
 * @param {unknown} error
 */
function logError(context, error) {
  console.error(`${LOG_PREFIX} ${context}`, error);
}

/**
 * @returns {boolean}
 */
function canCreateUuid() {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function';
}

/**
 * @param {any} cart
 * @returns {string | null}
 */
function readUuid(cart) {
  const value = cart?.attributes?.[CART_UUID_ATTRIBUTE];

  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * @param {any} cart
 * @returns {string | null}
 */
function readNote(cart) {
  const value = cart?.note;

  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * @param {any} cart
 * @returns {CartMetadata}
 */
function readMetadata(cart) {
  return { uuid: readUuid(cart), note: readNote(cart) };
}

/**
 * @returns {Promise<any>}
 */
async function readCart() {
  const url = `${Theme.routes.cart_url}.json`;

  logDebug('GET cart', { url });

  const response = await fetch(url, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(`Failed to read cart: ${response.status}`);

  const cart = await response.json();

  logDebug('cart read', {
    itemCount: cart?.item_count,
    existingUuid: readUuid(cart),
    existingNote: readNote(cart),
  });

  return cart;
}

/**
 * @param {Record<string, unknown>} payload
 * @returns {Promise<any>}
 */
async function writeCart(payload) {
  logDebug('POST cart update', { url: Theme.routes.cart_update_url, payload });

  const response = await fetch(Theme.routes.cart_update_url, {
    ...fetchConfig('json', { body: JSON.stringify(payload) }),
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(`Failed to update cart: ${response.status}`);

  const cart = await response.json();

  logDebug('cart update returned', readMetadata(cart));

  return cart;
}

/**
 * Eine Zeile je Warenkorbposition mit Mahlgrad:
 *   Mahlgrad: 1 x 100161000: Ganze Bohne
 * Ohne SKU faellt es auf die Varianten-ID zurueck, damit das Feld nie leer ist.
 * @param {any} cart
 * @returns {string[]}
 */
function noteSegments(cart) {
  const items = Array.isArray(cart?.items) ? cart.items : [];

  return items
    .filter((item) => item?.properties?.[MAHLGRAD_PROPERTY])
    .map((item) => {
      const sku = item.sku || String(item.variant_id);

      return `${NOTE_PREFIX} ${item.quantity} x ${sku}: ${item.properties[MAHLGRAD_PROPERTY]}`;
    });
}

/**
 * Setzt die Note neu zusammen: eigene Abschnitte werden ersetzt, alles andere
 * bleibt stehen. Kunden koennen ueber cart-note-component selbst eine Note
 * schreiben — die darf ein Mahlgrad-Update nicht loeschen.
 * @param {any} cart
 * @returns {string}
 */
function composeNote(cart) {
  return String(cart?.note ?? '')
    .split(NOTE_SPLITTER)
    .map((chunk) => chunk.trim())
    .filter((chunk) => chunk.length > 0 && !chunk.startsWith(NOTE_PREFIX))
    .concat(noteSegments(cart))
    .join(NOTE_SEPARATOR);
}

/**
 * Builds the smallest payload that brings the cart to the desired state, or
 * null when nothing needs writing.
 * @param {any} cart
 * @returns {Record<string, unknown> | null}
 */
function buildPayload(cart) {
  /** @type {Record<string, unknown>} */
  const payload = {};

  if (!readUuid(cart)) {
    if (!canCreateUuid()) throw new Error('crypto.randomUUID is unavailable');

    const uuid = crypto.randomUUID();

    logDebug('generated uuid', { uuid });
    payload.attributes = { [CART_UUID_ATTRIBUTE]: uuid };
  } else {
    logDebug('uuid already present, not overwriting', { uuid: readUuid(cart) });
  }

  const note = composeNote(cart);

  if ((cart?.note ?? '') !== note) {
    logDebug('note differs from target, will write', { current: readNote(cart), next: note });
    payload.note = note;
  } else {
    logDebug('note already matches target');
  }

  return Object.keys(payload).length > 0 ? payload : null;
}

/**
 * @returns {Promise<CartMetadata>}
 */
async function resolveCartMetadata() {
  const cart = await readCart();

  if (!cart?.item_count) {
    logDebug('skip: cart is empty');
    return readMetadata(cart);
  }

  const payload = buildPayload(cart);

  if (!payload) {
    logDebug('skip: cart already up to date');
    return readMetadata(cart);
  }

  return readMetadata(await writeCart(payload));
}

/**
 * Collapses concurrent callers onto a single read/write cycle so two triggers
 * can never generate two uuids for the same cart.
 * @returns {Promise<CartMetadata>}
 */
export function ensureCartMetadata() {
  if (pendingSync) {
    logDebug('reusing in-flight sync cycle');
    return pendingSync;
  }

  const startedAt = performance.now();

  pendingSync = resolveCartMetadata().finally(() => {
    pendingSync = null;
    logDebug('sync cycle finished', { durationMs: Math.round(performance.now() - startedAt) });
  });

  return pendingSync;
}

/**
 * @param {string} trigger
 * @returns {Promise<CartMetadata | null>}
 */
function ensureCartMetadataQuietly(trigger) {
  logDebug('sync requested', { trigger });

  return ensureCartMetadata()
    .then((metadata) => {
      logDebug('sync resolved', { trigger, ...metadata });
      return metadata;
    })
    .catch((error) => {
      logError(`Could not sync cart metadata (${trigger}):`, error);
      return null;
    });
}

/**
 * @param {Event} event
 */
function handleCartLinesUpdate(event) {
  const settled = /** @type {any} */ (event).promise;

  logDebug('cart lines update observed', { hasEventPromise: Boolean(settled) });

  Promise.resolve(settled)
    .then(() => ensureCartMetadataQuietly(TRIGGER.cartLinesUpdate))
    .catch(() => {
      logDebug('cart lines update rejected, skipping sync');
    });
}

/**
 * @param {EventTarget | null} submitter
 * @returns {submitter is HTMLButtonElement | HTMLInputElement}
 */
function isCheckoutSubmitter(submitter) {
  return (
    (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) &&
    submitter.name === CHECKOUT_SUBMITTER_NAME
  );
}

/**
 * Holds the checkout submission until the metadata is persisted, then replays
 * it with the original submitter so Shopify still routes to checkout.
 * @param {SubmitEvent} event
 */
async function handleCheckoutSubmit(event) {
  const form = event.target;
  const submitter = event.submitter;

  if (!(form instanceof HTMLFormElement) || !isCheckoutSubmitter(submitter)) return;

  if (formsAwaitingResubmit.has(form)) {
    formsAwaitingResubmit.delete(form);
    logDebug('checkout resubmit passing through', { formId: form.id });
    return;
  }

  event.preventDefault();

  logDebug('checkout intercepted, waiting for cart metadata', { formId: form.id });

  const metadata = await ensureCartMetadataQuietly(TRIGGER.checkout);

  formsAwaitingResubmit.add(form);

  logDebug('checkout released', { formId: form.id, ...metadata });

  form.requestSubmit(submitter);
}

function init() {
  document.addEventListener(StandardEvents.cartLinesUpdate, handleCartLinesUpdate);
  document.addEventListener('submit', handleCheckoutSubmit, { capture: true });

  logDebug('initialised, listeners attached');

  ensureCartMetadataQuietly(TRIGGER.pageLoad);
}

logDebug('module evaluated', { readyState: document.readyState });

onDocumentReady(init, { once: true });
